import authService from 'Services/api/auth.service'
import ROUTE from 'constant/route'
import httpRequest from 'httpRequest'
import { LoginContract } from 'models'
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { finalizeUserLoginFlow, getActionCode } from 'store/authCommon'
import { VALIDATE_NON_CLIENT_TOKEN_SUCCESS } from 'store/reducers/auth.reducer'
import { isExceedLogin } from 'utilities/commonError'
import errorMessages from 'utilities/errorMessages'
import { isNonClientTokenType } from 'utilities/jwtHelper'

enum LoginMethod {
    UNDEFINED,
    USERNAME,
    PIN,
}

interface ILoginContext {
    /** True if login is processing */
    isDoingLogin: boolean

    /** store error message from API. */
    loginError: string | null

    /** TRUE means we're warning user is trying to do duplication login */
    isWarningDuplicationLogin: boolean

    /** called when user login with username/password */
    loginWithUsername: (payload: LoginContract) => Promise<void>

    /** called when user login with PIN */
    loginWithPIN: (pin: string) => Promise<void>

    /** called when user decide to force login. Which cause other device to be logged out consequently. */
    forceLogin: () => Promise<void>

    /** called when user cancel force login */
    cancelForceLogin: () => void
    resetLoginError: () => void
}

export const LoginContext = createContext<ILoginContext>({
    isDoingLogin: false,
    loginError: null,
    isWarningDuplicationLogin: false,
    loginWithUsername: async () => {},
    loginWithPIN: async () => {},
    forceLogin: async () => {},
    cancelForceLogin: () => {},
    resetLoginError: () => {},
})

function useLogin(): ILoginContext {
    const lastLoginCredential = useRef<LoginContract | string | null>(null)
    const lastLoginMethod = useRef<LoginMethod>(LoginMethod.UNDEFINED)
    const [isDoingLogin, setIsDoingLogin] = useState(false)
    const [isWarningDuplicationLogin, setIsWarningDuplicationLogin] = useState(
        false,
    )
    const [loginError, setLoginError] = useState<string | null>(null)

    const dispatch = useDispatch()
    const history = useHistory()

    const handleLoginError = useCallback((error: any) => {
        if (isExceedLogin(error)) setIsWarningDuplicationLogin(true)
        else {
            const { message } = errorMessages(error)
            setLoginError(message)
        }
    }, [])

    const onProcessLoginAction = useCallback(
        async (loginAction: () => Promise<void>) => {
            setLoginError(null)
            setIsDoingLogin(true)

            try {
                await loginAction()
            } catch (err: any) {
                handleLoginError(err)
            }

            setIsDoingLogin(false)
        },
        [handleLoginError],
    )

    const loginWithUsername = useCallback(
        (payload: LoginContract) =>
            onProcessLoginAction(async () => {
                lastLoginCredential.current = payload
                lastLoginMethod.current = LoginMethod.USERNAME

                const authResult = (await authService.login(payload)).data
                if (!authResult.token)
                    throw new Error(
                        'no proper credential information return from login API',
                    )

                if (isNonClientTokenType(authResult.token)) {
                    httpRequest.setNonClientAuthorizationCookies(
                        authResult.token,
                    )

                    // set authState = `SUCCESS_NON_CLIENT`
                    dispatch({ type: VALIDATE_NON_CLIENT_TOKEN_SUCCESS })
                    await getActionCode(dispatch)
                    // redirect to select device page
                    history.replace(ROUTE.SELECT_DEVICE)
                } else {
                    await finalizeUserLoginFlow(authResult, dispatch, history)
                }
            }),
        [dispatch, history, onProcessLoginAction],
    )

    const loginWithPIN = useCallback(
        (pin: string) =>
            onProcessLoginAction(async () => {
                lastLoginCredential.current = pin
                lastLoginMethod.current = LoginMethod.PIN

                const authResult = (await authService.loginPin(pin)).data

                await finalizeUserLoginFlow(authResult, dispatch, history)
            }),
        [dispatch, history, onProcessLoginAction],
    )

    const forceLogin = useCallback(async () => {
        if (lastLoginMethod.current === LoginMethod.USERNAME) {
            if (!lastLoginCredential.current) return

            const credential = lastLoginCredential.current as LoginContract
            await authService.logoutAllByUsername(credential)
            await loginWithUsername(credential)
        } else if (lastLoginMethod.current === LoginMethod.PIN) {
            if (!lastLoginCredential.current) return

            const pin = lastLoginCredential.current as string

            await authService.logoutAllByPin(pin)
            await loginWithPIN(pin)
        }
    }, [loginWithPIN, loginWithUsername])

    const cancelForceLogin = useCallback(() => {
        setIsWarningDuplicationLogin(false)
    }, [])

    const resetLoginError = useCallback(() => {
        setLoginError(null)
    }, [])

    return {
        isDoingLogin,
        loginError,
        isWarningDuplicationLogin,
        loginWithUsername,
        loginWithPIN,
        forceLogin,
        cancelForceLogin,
        resetLoginError,
    }
}

function LoginProvider({ children }: { children: React.ReactNode }) {
    const loginContext = useLogin()

    return (
        <LoginContext.Provider value={loginContext}>
            {children}
        </LoginContext.Provider>
    )
}

export default LoginProvider

export const useLoginContext = () => useContext(LoginContext)
